import React, { useEffect, useState } from "react";
import { Grid } from "@mui/material";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Typography from "@mui/material/Typography";
import Alert from "@mui/material/Alert";
import CircularProgress from "@mui/material/CircularProgress";
import Box from "@mui/material/Box";
import { useNavigate, useLocation } from "react-router-dom";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import axios from "axios";

const API_BASE_URL = "https://jobmanagement-497524552274.us-central1.run.app";

const JobTrendCard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [jobsByStatus, setJobsByStatus] = useState({
    CANCELLED: [],
    RESCHEDULED: [],
    CREATED: 0,
    COMPLETED: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const headerTitle = location?.state;

  useEffect(() => {
    const fetchJobStatusCount = async () => {
      // FIXED: Changed from "authToken" to "token"
      const token = localStorage.getItem("token");
      
      if (!token) {
        console.log("No auth token found in localStorage");
        setError("Authentication token not found. Please log in again.");
        navigate("/Login");
        return;
      }

      console.log("Making request with token:", token.substring(0, 20) + "...");
      
      try {
        const response = await axios.get(`${API_BASE_URL}/api/jobs/status-count`, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          timeout: 5000,
        });

        console.log("API Response:", response.data);

        if (response.status === 403) {
          setError("You don't have permission to access this resource. Please contact your administrator.");
          return;
        }

        setJobsByStatus(response.data);
        setError(null);
      } catch (err) {
        console.error("API Error:", err);
        console.error("Error Response:", err.response);
        
        if (err.response) {
          switch (err.response.status) {
            case 401:
              setError("Your session has expired. Please log in again.");
              navigate("/Login");
              break;
            case 403:
              setError("You don't have permission to access this resource. Please check your credentials.");
              break;
            default:
              setError(`Failed to fetch job status counts: ${err.response.data?.message || 'Unknown error'}`);
          }
        } else if (err.code === 'ECONNABORTED') {
          setError("Request timed out. Please try again.");
        } else {
          setError("Unable to connect to the server. Please check your connection.");
        }
      } finally {
        setLoading(false);
      }
    };

    fetchJobStatusCount();
  }, [navigate]);

  const totalJobs = jobsByStatus.CREATED + jobsByStatus.COMPLETED;
  const createdPercentage = totalJobs ? ((jobsByStatus.CREATED / totalJobs) * 100).toFixed(1) : 0;
  const completedPercentage = totalJobs ? ((jobsByStatus.COMPLETED / totalJobs) * 100).toFixed(1) : 0;

  const chartData = [
    { name: "Created", value: jobsByStatus.CREATED, fill: "#FE9400" },
    { name: "Completed", value: jobsByStatus.COMPLETED, fill: "#1976d2" },
  ];

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Grid container spacing={3} sx={{ margin: "5%", bgcolor: "#FFFFFF" }}>
      {error && (
        <Grid item xs={12}>
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        </Grid>
      )}
      
      <Grid item xs={12}>
        <Typography variant="h6" component="h2" sx={{ mb: 3 }}>
          {headerTitle || "Job Trends"}
        </Typography>
      </Grid>
      
      <Grid item xs={12} md={6}>
        <Card>
          <CardContent>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
              Job Status Distribution
            </Typography>
            <Box sx={{ height: 200, width: '100%' }}>
              <ResponsiveContainer>
                <BarChart data={chartData}>
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="value" />
                </BarChart>
              </ResponsiveContainer>
            </Box>
            <Typography variant="body2" sx={{ mt: 2 }}>
              Created: {jobsByStatus.CREATED} ({createdPercentage}%)
            </Typography>
            <Typography variant="body2">
              Completed: {jobsByStatus.COMPLETED} ({completedPercentage}%)
            </Typography>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};

export default JobTrendCard;